/**
 * QR Screen — PXP Exchange Module
 *
 * Extracted from QRScreen.js (Step 2 of qr-screen-refactor).
 * Handles both sides of Profile-to-Profile exchange:
 *   - Host: receives profile_exchange from guest, sends response, navigates
 *   - Guest: scans host QR, joins channel, sends profile_exchange, waits for response
 */

import { getAnonUserId } from '../../utils/identityUtils';
import { joinChatRoom } from '../../services/chat/chatService';
import { currentUtcOffsetMinutes } from '../../utils/dateHelpers';
import {
  logFunnelQRScanned,
  logConnectionLocation,
  logConnectionError,
  logEvent,
} from '../../services/analytics/eventLogger';
import { handOffNearbyTiming } from '../../services/proximity/nearbyTiming';
import { upsertConnectionView } from '../../services/storage/userDataStorage';
import { MEETING_KIND } from '../../services/meetings/meetingRule';
import { stageConnectSheets } from '../../services/exchange/connectSheets';
import {
  claimQrRoom,
  getActiveSession,
  confirmGuestConnection,
  findExistingConnection,
  recordPxpGuest,
} from '../../services/activeSession';
import {
  decideHostSide,
  writeHostRecord,
  sendHostReply,
  writeGuestRecord,
  sendKnock,
} from './pxpSides';
import { startGuestOffer } from './pxpOffer';
import { busyTitle, firstNameOf } from '../../utils/contactUtils';

/**
 * A line in the scanner that clears after a moment — unless a newer line (a
 * rescan) has replaced it.
 */
export const COULD_NOT_CONNECT = "Couldn't connect — try again";

export const showScanLine = (setScanMessage, message, ms = 4000) => {
  setScanMessage(message);
  setTimeout(() => setScanMessage((now) => (now === message ? null : now)), ms);
};

/**
 * Handle PXP Host exchange: guest scanned our QR and sent profile_exchange.
 *
 * @param {Object} payload - The profile_exchange message payload
 * @param {Object} config - Explicit dependencies (previously closures)
 * @param {string} config.chatRoomId
 * @param {string} config.profileUserId
 * @param {React.RefObject} config.profileRef
 * @param {React.RefObject} config.locationDataRef
 * @param {React.RefObject} config.locationPromiseRef
 * @param {Function} config.updateAnonData
 * @param {Function} config.updateConnectionMetadata
 * @param {Function} config.updateSessionChatRoomId
 * @param {Function} config.navigateTo
 * @param {Function} config.onClose
 * @param {Function} config.confirmHostConnection
 * (The new design never comes here on a knock: pxpOffer.prepareHostOffer, E3b-2a.)
 */
export const handlePXPHostExchange = async (payload, config) => {
  const {
    chatRoomId,
    profileUserId,
    profileRef,
    locationDataRef,
    locationPromiseRef,
    coordsPromiseRef,
    updateAnonData,
    updateConnectionMetadata,
    updateSessionChatRoomId,
    navigateTo,
    onClose,
    confirmHostConnection,
  } = config;

  console.log('[QR_SCREEN] PXP Host: Received profile_exchange from guest');

  // ─── Duplicate detection + the meeting verdict, before any writes (pxpSides) ─
  const { existingChatRoomId, verdict, pxpMetadata, pxpTimestamp } = await decideHostSide(payload, {
    profileUserId,
    locationDataRef,
    coordsPromiseRef,
  });
  const pxpLocationData = locationDataRef.current;
  const isReconnection = !!existingChatRoomId;
  const targetChatRoomId = isReconnection ? existingChatRoomId : chatRoomId;

  // ─── Build fresh profile data ──────────────────────────────────────────────
  const freshProfileData = payload.profileData
    ? { ...payload.profileData, senderProfileUserId: payload.senderProfileUserId }
    : null;

  // ─── Write data to target chatRoomId (pxpSides) ───────────────────────────
  await writeHostRecord({
    isReconnection,
    verdict,
    chatRoomId,
    targetChatRoomId,
    profileUserId,
    freshProfileData,
    pxpMetadata,
    pxpTimestamp,
    trigger: payload.trigger,
    updateAnonData,
    updateConnectionMetadata,
    locationDataRef,
    locationPromiseRef,
  });

  // ─── First connection: the room record + the scanner as its guest BEFORE the
  // reply — under the open-access lockdown only participants read a room, and
  // the scanner must be able to read this reply to confirm their side ────────
  if (!isReconnection) {
    await confirmHostConnection(chatRoomId, profileUserId, 'pxp');
    await recordPxpGuest(chatRoomId, payload.senderProfileUserId);
  }

  // ─── Send profile_data_response on NEW channel (guest is listening there) ──
  const currentProfile = await sendHostReply({
    profileRef,
    isReconnection,
    chatRoomId,
    targetChatRoomId,
    profileUserId,
    verdict,
    pxpMetadata,
  });

  // ─── Navigation + confirmation ─────────────────────────────────────────────
  if (!isReconnection) {
    // First connection: both sheets ("Where you met", then share)
    // (the room record was confirmed before the reply, above)
    upsertConnectionView(chatRoomId, profileUserId, { metadata: pxpMetadata });
    logFunnelQRScanned(chatRoomId, currentProfile?.name, payload.trigger);
    if (pxpLocationData) {
      logConnectionLocation(chatRoomId, pxpLocationData, 'radar');
    }
  }
  // Sheets on every connect (signup-meetings § What each shows): "Where you met" on a
  // first meeting or a re-meet, the share sheet always. No confirm writes on a repeat.
  stageConnectSheets(targetChatRoomId, verdict.kind, { repeat: isReconnection });

  updateSessionChatRoomId(targetChatRoomId, true);
  // A nearby connect: time it on the chat page like a scan (nearbyTiming)
  if (payload.trigger === 'proximity') handOffNearbyTiming(targetChatRoomId, currentProfile?.name);
  console.log(
    `[QR_SCREEN] PXP Host: Navigating to ProfileConnectionPage (reconnection: ${isReconnection})`
  );
  navigateTo('profile-connection-page');
  if (onClose) {
    onClose();
  }
};

/**
 * Handle PXP Guest scan: we scanned another Profile user's QR code.
 *
 * @param {string} decodedText - Raw text from QR scanner
 * @param {Object} config - Explicit dependencies (previously closures)
 * @param {string} config.profileUserId
 * @param {React.RefObject} config.profileRef
 * @param {React.RefObject} config.locationDataRef
 * @param {React.RefObject} config.locationPromiseRef
 * @param {Function} config.updateAnonData
 * @param {Function} config.updateConnectionMetadata
 * @param {Function} config.updateSessionChatRoomId
 * @param {Function} config.navigateTo
 * @param {Function} config.onClose
 * @param {Function} config.setScanMessage
 * @param {Function} config.setShowScanner
 * @param {string} [config.trigger] - 'in_app_scan' | 'link_scan' | 'proximity'
 * @param {boolean} [config.newLanding] - the new experience: a known person opened off
 *   their code (not showing it) opens their new Detail page
 * @param {Function} [config.onOffer] - the new experience (E3b-2a): the offer goes to
 *   this phone's own code page, and nothing is recorded until both have tapped
 *   (pxpOffer.startGuestOffer)
 * @param {Function} [config.onBusy] - with onOffer, a nearby match whose host is busy (P4)
 */
export const handlePXPGuestScan = async (decodedText, config) => {
  const {
    profileUserId,
    profileRef,
    locationDataRef,
    locationPromiseRef,
    updateAnonData,
    updateConnectionMetadata,
    updateSessionChatRoomId,
    navigateTo,
    onClose,
    setScanMessage,
    setShowScanner,
    trigger,
    newLanding = false,
    onOffer = null,
    onBusy = null,
  } = config;

  // Clear any previous scan message
  setScanMessage(null);

  // 1. Parse URL
  let url;
  try {
    url = new URL(decodedText);
  } catch {
    showScanLine(setScanMessage, { type: 'error', text: 'Not a valid QR code' }, 3000);
    return;
  }

  // 2. Check if it's a Royale URL with /u/{profileUserId} path
  const match = url.pathname.match(/^\/u\/(.+)$/);
  if (!match) {
    showScanLine(setScanMessage, { type: 'error', text: 'Not a Royale QR code' }, 3000);
    return;
  }

  const hostProfileUserId = match[1];

  // 3. Self-scan detection
  if (hostProfileUserId === profileUserId) {
    showScanLine(
      setScanMessage,
      { type: 'info', title: 'Oops', text: "That's your QR code!" },
      3000
    );
    return;
  }

  // 4. Valid PXP scan. The new code page's in-app scanner stays open until the
  // scan lands, saying so (9a, Mark 2026-10-30: the scanning phone had no idea
  // if it worked); onOffer closes it. The old design closes it at once.
  const keepOpen = !!onOffer && trigger === 'in_app_scan';
  if (keepOpen) {
    setScanMessage({ type: 'info', text: 'Connecting…' });
  } else {
    setShowScanner(false);
    setScanMessage(null);
  }
  console.log('[QR_SCREEN] PXP Guest: Scanned host profileUserId:', hostProfileUserId);

  try {
    // ─── Duplicate detection + session lookup (independent — run together) ──
    // Both profile IDs available: own profileUserId (config) + hostProfileUserId (URL).
    // getAnonUserId() returns null for users who were never Anon. Detection is
    // known even when the host isn't showing their QR, so a known person still
    // lands on their connection (invite-and-rescan §2 / signup-connect step 3).
    const [existingChatRoomId, session] = await Promise.all([
      findExistingConnection(profileUserId, hostProfileUserId, getAnonUserId()).catch((err) => {
        // Detection failed on guest side — throw to abort exchange.
        // Caller (useProximity or QRScreen) handles the error UI.
        console.error('[QR_SCREEN] PXP Guest: Detection query failed:', err);
        throw err;
      }),
      // 5. Session lookup — find host's active chatRoomId
      getActiveSession(hostProfileUserId),
    ]);
    if (existingChatRoomId) {
      console.log(
        '[QR_SCREEN] PXP Guest: Reconnection detected, existing chatRoomId:',
        existingChatRoomId
      );
    }
    const first = firstNameOf(session?.name);
    if (keepOpen && first) setScanMessage({ type: 'info', text: `Connecting with ${first}…` });

    // One exchange per code at a time (052): claim the host's live room before
    // joining or writing — a reconnect too, since its handshake writes into it.
    // Someone else mid-exchange → the busy screen (Back to People), never their
    // Detail page (Mark, 2026-10-21). A code that closed meanwhile, or a failed
    // call (logged by postScannerRpc), is a code that isn't open.
    const claim = session?.chat_room_id ? await claimQrRoom(session.chat_room_id) : null;
    if (claim === 'busy') {
      console.log('[QR_SCREEN] PXP Guest: Host is mid-exchange with someone else');
      // A nearby match on the new design: the busy line on this phone's own
      // waiting sheet, then back to the code (P4) — not the busy screen
      if (onOffer && onBusy && trigger === 'proximity') {
        onBusy(session.name);
        return;
      }
      // The new code page's scanner: the busy line in the open camera (9a)
      if (keepOpen) {
        // The event the busy screen logs, kept now that the camera shows it
        logEvent('qr_room_busy', { hostProfileUserId, inApp: true });
        showScanLine(setScanMessage, { type: 'error', text: busyTitle(session.name) });
        return;
      }
      navigateTo('user-redirect', {
        profileUserId: hostProfileUserId,
        busyName: session.name,
        inApp: true,
      });
      if (onClose) onClose();
      return;
    }

    if (claim !== 'claimed') {
      if (existingChatRoomId) {
        // Host isn't on their QR, but we already know them: open the
        // connection we have (no exchange, no writes)
        console.log('[QR_SCREEN] PXP Guest: Host not on QR — opening existing connection');
        setShowScanner(false);
        setScanMessage(null);
        updateSessionChatRoomId(existingChatRoomId, true);
        if (newLanding) {
          navigateTo('new-detail', { chatRoomId: existingChatRoomId, source: 'connection-list' });
        } else {
          navigateTo('profile-connection-page');
        }
        if (onClose) onClose();
        return;
      }
      console.log('[QR_SCREEN] PXP Guest: Host has no active session');
      if (keepOpen) showScanLine(setScanMessage, { type: 'error', text: COULD_NOT_CONNECT });
      return;
    }

    const hostChatRoomId = session.chat_room_id;
    console.log('[QR_SCREEN] PXP Guest: Found host session, chatRoomId:', hostChatRoomId);

    // The new design (E3b-2a): nothing is recorded until both have tapped —
    // the offer takes it from here, on this phone's own code page (pxpOffer)
    if (onOffer) {
      await startGuestOffer({ ...config, hostProfileUserId, session, existingChatRoomId });
      return;
    }

    const isReconnection = !!existingChatRoomId;
    const targetChatRoomId = isReconnection ? existingChatRoomId : hostChatRoomId;

    // 6. Set up message handler — closure captures isReconnection + targetChatRoomId
    const handleHostMessage = async (payload) => {
      if (payload.senderProfileUserId === profileUserId) return;

      if (payload.type === 'profile_data_response') {
        console.log(
          `[QR_SCREEN] PXP Guest: Received host profile data (reconnection: ${isReconnection})`
        );

        const freshProfileData = payload.profileData
          ? { ...payload.profileData, senderProfileUserId: payload.senderProfileUserId }
          : null;

        // The host decided the meeting; this phone adopts its key and start time so
        // both phones file one meeting the same way. A host on an old app sends no
        // verdict: say so, and write no meeting row (never decide alone).
        const guestTimestamp = new Date().toISOString();
        if (!payload.meeting) {
          logConnectionError('meeting_verdict_missing', {
            chatRoomId: targetChatRoomId,
            isReconnection,
          });
        }
        // An old-app host's reply carries no verdict: no key (so no meeting row), now
        const meeting = payload.meeting || {
          kind: isReconnection ? MEETING_KIND.RECONNECT : MEETING_KIND.FIRST,
          metAt: guestTimestamp,
          metUtcOffset: currentUtcOffsetMinutes(),
        };
        const { kind } = meeting;
        const guestMetadata = await writeGuestRecord({
          meeting,
          isReconnection,
          targetChatRoomId,
          hostChatRoomId,
          profileUserId,
          freshProfileData,
          locationDataRef,
          locationPromiseRef,
          updateAnonData,
          updateConnectionMetadata,
        });
        if (!isReconnection) {
          // First connection: write to connections table (sheets staged below)
          confirmGuestConnection(hostChatRoomId, hostProfileUserId, profileUserId, 'pxp').then(
            () => {
              upsertConnectionView(hostChatRoomId, profileUserId, { metadata: guestMetadata });
            }
          );
        }
        updateSessionChatRoomId(targetChatRoomId, true);
        if (trigger === 'proximity') {
          handOffNearbyTiming(targetChatRoomId, profileRef.current?.name);
        }
        // Sheets on every connect — the host's verdict decides "Where you met"
        stageConnectSheets(targetChatRoomId, kind, { repeat: isReconnection });
        console.log(
          `[QR_SCREEN] PXP Guest: Navigating to ProfileConnectionPage (reconnection: ${isReconnection})`
        );
        navigateTo('profile-connection-page');
        if (onClose) {
          onClose();
        }
      }
    };

    // 7. Join host's WebSocket channel as guest
    await joinChatRoom(hostChatRoomId, handleHostMessage);
    console.log('[QR_SCREEN] PXP Guest: Joined host channel');
    // A re-meet host replies in the room we already share (the lockdown lets
    // only participants read the QR room) — listen there too, before sending
    if (existingChatRoomId) {
      await joinChatRoom(existingChatRoomId, handleHostMessage);
      console.log('[QR_SCREEN] PXP Guest: Joined existing room for a re-meet reply');
    }

    // 8. Send profile_exchange message with our name + photo (pxpSides)
    await sendKnock({ hostChatRoomId, profileUserId, profileRef, isReconnection, trigger });
  } catch (err) {
    console.error('[QR_SCREEN] PXP Guest: Exchange error:', err);
    throw err; // Re-throw so caller (useProximity / QRScreen) can handle UI
  }
};
